// @flow
import type { Breadcrumb } from 'esm-web-pro/redux/breadcrumb/reducer';
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Box, Heading, Text } from 'grommet';
import type { ApiClient } from 'speed-js-react';
import styled from 'styled-components';
import {
    defineMessages, FormattedMessage, IntlShape, injectIntl,
} from 'react-intl';
import { connectAPI } from 'speed-js-react';
import permissionRequired from 'esm-web-pro/decorators/permissionRequired';
import MapEditor from 'esm-web-pro/containers/map/Editor';
import { addBreadcrumbs, removeBreadcrumb } from 'esm-web-pro/redux/breadcrumb';
import { MapViewers } from 'esm-web-pro/containers/map';
import type { Map, Resource } from 'esm-web-pro/containers/map/types';


const messages = defineMessages({
    'addresses-map-btn': {
        id: 'addresses-map-btn',
        defaultMessage: 'Map',
    },
    'addresses-map-btn-plural': {
        id: 'addresses-map-btn-plural',
        defaultMessage: 'Maps',
    },
    'maps-page-title': {
        id: 'maps-page-title',
        defaultMessage: 'Maps',
    },
});


type Props = {
    intl: IntlShape,
    addBreadcrumbs: (Array<Breadcrumb>) => void,
    removeBreadcrumb: (title: string) => void,
    getMaps: () => void,
    unlock: (identity: string) => Promise<Object>,
    maps: Array<Map>,
}

type State = {
    resources: Array<Resource>,
    editedLayer: Object | null,
    hasEditor: boolean,
}

const StyledBox = styled(Box)`
    position: relative;
`;

const MapDispatchToProps = ((dispatch) => (bindActionCreators({ addBreadcrumbs, removeBreadcrumb }, dispatch)));

const mapResource = {
    name: 'address.map',
    label: messages['addresses-map-btn'],
    labelPlural: messages['addresses-map-btn-plural'],
    type: 'polygon',
    geometryField: 'bounds',
    query: {
        geojson: true,
        ordering: 'name',
        nonested: 1,
        valid_to__isnull: true,
        valid_from__isnull: false,
    },
    displayFields: ['name'],
    allowNew: true,
    allowEdit: true,
    allowDelete: true,
    key: 'map',
};

@connect(null, MapDispatchToProps)
@connectAPI((apiClient: ApiClient) => ({
    getMaps: {
        send: () => apiClient.resources['address.map'].list({
            ordering: 'name',
            nonested: 1,
            valid_to__isnull: true,
            valid_from__isnull: false,
            all: true,
        }),
        success: (response) => ({
            maps: response.results,
        }),
    },
    unlock: {
        send: (identity: string) => apiClient.resources['networks.map'].unlock(identity),
    },
}))
class MapsPage extends React.Component<Props, State> {
    static defaultProps = {
        maps: [],
    };

    editedMaps: { [string]: boolean } = {};

    state = {
        resources: [
            mapResource,
        ],
        editedLayer: null,
        hasEditor: false,
    };

    componentDidMount() {
        const {
            addBreadcrumbs: addBreadcrumbsAction, intl: { formatMessage }, getMaps,
        } = this.props;
        addBreadcrumbsAction([
            {
                title: formatMessage(messages['maps-page-title']),
                link: '/administration/maps/maps',
            },
        ]);
        getMaps();
    }

    componentWillUnmount() {
        const {
            removeBreadcrumb: removeBreadcrumbAction, intl: { formatMessage },
        } = this.props;
        const { editedLayer } = this.state;
        removeBreadcrumbAction(formatMessage(messages['maps-page-title']));

        if (editedLayer) {
            const { unlock } = this.props;
            Object.values(editedLayer.properties.maps).forEach((map) => unlock(map.identity));
        }
    }

    unlockMaps = async (maps) => {
        const { unlock, getMaps } = this.props;
        const promises = [];

        maps.forEach((map) => {
            promises.push(unlock(map.identity));
        });

        await Promise.all(promises);
        await getMaps();
    };

    handleEditFromOtherUser = (mapIdentity: string, users: Array<string>) => {
        this.editedMaps[mapIdentity] = !!users;
        const edited = Object.keys(this.editedMaps).some((uid) => this.editedMaps[uid]);

        if (edited) {
            this.setState({
                resources: [
                    { ...mapResource, allowEdit: false, allowDelete: false },
                ],
            });
            this.setState({ hasEditor: true });
        } else {
            this.setState({
                resources: [
                    { ...mapResource, allowEdit: true, allowDelete: true },
                ],
            });
            this.setState({ hasEditor: false });
        }
    };

    handleEditStart = (layer: Object) => {
        this.setState({
            editedLayer: layer.feature,
        });
    };

    handleEditEnd = async () => {
        const { editedLayer } = this.state;
        await this.unlockMaps(editedLayer.properties.maps);
        this.setState({
            editedLayer: null,
        });
    };

    render() {
        const { maps } = this.props;
        const { resources, editedLayer, hasEditor } = this.state;
        return (
            <Box pad="small" height="100%">
                <Heading level="3" size="small">
                    <FormattedMessage id="addresses-map" defaultMessage="Maps" />
                </Heading>
                {/* <Box pad={{ top: 'small', horizontal: 'xsmall', bottom: 'none' }}> */}
                <StyledBox height="100%">
                    <Box
                        flex={{ grow: 1 }}
                        height="100px"
                        overflow="scroll"
                        direction="row"
                        wrap
                        pad={{ bottom: '30px' }}
                    >
                        {
                            maps.map((map) => {
                                const isDraft = !!(
                                    editedLayer && !!Object.values(editedLayer.properties.maps).find(
                                        (currMap) => currMap.identity === map.identity,
                                    )
                                );
                                let _map = map;
                                if (isDraft !== map.is_draft) {
                                    _map = { ...map, is_draft: isDraft };
                                }
                                return (
                                    <Box
                                        direction="row"
                                        justify="end"
                                        flex={{ shrink: 0 }}
                                        key={map.uuid}
                                    >
                                        <Box alignContent="start" alignSelf="center">
                                            {`${map.name} 
                                            - ${map.type === 'networks' ? 'Инфраструктура' : 'Покрития'}`}
                                        </Box>
                                        <MapViewers
                                            map={_map}
                                            sendOpenedBy={false}
                                            onEditFromOtherUser={
                                                (users: Array<string>) => this.handleEditFromOtherUser(
                                                    map.identity, users,
                                                )
                                            }
                                            key={map.uuid}
                                            lockActive
                                        />
                                        <Box alignContent="start" alignSelf="center" pad={{ horizontal: '5px' }}>
                                            <Text>|</Text>
                                        </Box>
                                    </Box>
                                );
                            })
                        }
                    </Box>
                    {hasEditor
                        && (
                            <Box
                                pad={{ top: '5px', bottom: '15px', horizontal: '20px' }}
                                margin={{ top: '10px', bottom: '20px' }}
                                background="status-warning"
                                round
                                alignSelf="start"
                            >
                                <Text color="white">
                                    <FormattedMessage
                                        id="cant-edit-map"
                                        defaultMessage="Cannot edit any map because there are
                                         maps already being edited"
                                    />
                                </Text>
                            </Box>
                        )
                    }
                    <Box height="100%">
                        <MapEditor
                            zoom={14}
                            // Plovdiv
                            center={[42.1380295, 24.7493895]}
                            resources={resources}
                            onEditStart={this.handleEditStart}
                            onEditEnd={this.handleEditEnd}
                        />
                    </Box>
                </StyledBox>
                {/* </Box> */}
            </Box>
        );
    }
}


export default permissionRequired(['addresses.add_map', 'addresses.change_map'])(injectIntl(MapsPage));
